import is from '@sindresorhus/is';

import { NetworkError } from './network-error';

const REPORT_THROTTLE_MS = 60_000;

/**
 * `attempt` is a single failed request nobody claimed; `terminal` is a logical operation that
 * never recovered, and carries the attempt count and the cause.
 */
export type NetworkErrorReportKind = 'attempt' | 'terminal';

type ReportSlot = {
    readonly kind: NetworkErrorReportKind;
    readonly reportedAt: number;
};

const claimedNetworkErrors = new WeakSet<NetworkError>();
const slotsByEndpoint = new Map<string, ReportSlot>();

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;

/**
 * Claims responsibility for reporting a network failure, so the fetcher stays silent about it.
 * The retry layer claims every attempt it sees and reports once its retries are exhausted; a
 * caller that deliberately ignores a failure (fire-and-forget telemetry) claims it too.
 *
 * A failure nobody claims is reported by the fetcher on the next macrotask, so an imperative
 * call site with no retry layer still surfaces it without anyone opting in.
 */
export const claimNetworkErrorReport = (error: unknown): void => {
    if (error instanceof NetworkError) {
        claimedNetworkErrors.add(error);
    }
};

export const isNetworkErrorReportClaimed = (error: NetworkError): boolean => claimedNetworkErrors.has(error);

const normalizeSegment = (segment: string): string =>
    UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment) ? '{id}' : segment;

// The query string is dropped and id-shaped segments are collapsed, so the pages of one
// paginated call and the per-resource polls of one route all describe a single endpoint.
// Any all-digit segment counts as an id, but among uuid shapes only the canonical hyphenated one.
const toEndpointKey = (error: NetworkError): string => {
    try {
        const url = new URL(error.url);
        const pathname = url.pathname.split('/').map(normalizeSegment).join('/');

        return `${error.method} ${url.origin}${pathname}`;
    } catch {
        return `${error.method} ${error.url}`;
    }
};

/**
 * A single slot per endpoint per window, shared by everyone who may report a network failure.
 * An outage makes the same endpoint fail on every poll, on every page of a fan-out and on
 * every attempt that leaked past its claim, and all of those describe one incident.
 *
 * A terminal report still preempts a slot an attempt-level report is holding: it describes the
 * same incident with the attempt count and cause attached, so it must not be the one dropped.
 */
export const takeNetworkErrorReportSlot = (error: NetworkError, kind: NetworkErrorReportKind): boolean => {
    const now = Date.now();
    const key = toEndpointKey(error);

    for (const [slotKey, slot] of slotsByEndpoint) {
        if (now - slot.reportedAt >= REPORT_THROTTLE_MS) {
            slotsByEndpoint.delete(slotKey);
        }
    }

    const held = slotsByEndpoint.get(key);

    if (!is.undefined(held) && (kind === 'attempt' || held.kind === 'terminal')) {
        return false;
    }

    slotsByEndpoint.set(key, { kind, reportedAt: now });

    return true;
};
